/**
 * Maps the stable machine `type` code the BFF's credential-auth Problem
 * responses carry (apps/api/src/modules/bff/routes.ts `sendCredentialProblem`
 * / `CREDENTIAL_ERROR_STATUS`) to the UI copy the Gate 2 UX spec calls for
 * (§2.2 "Login" error states — "each with a distinct recovery action").
 *
 * The server's own `detail` text is ALREADY safe/non-enumerating (C19) and is
 * used as the fallback for any code this map doesn't recognize, so an
 * unmapped/future code still renders something reasonable rather than a bare
 * "Something went wrong."
 */
export interface ProblemCopy {
  readonly title: string
  readonly detail: string
}

const LOGIN_PROBLEM_COPY: Record<string, ProblemCopy> = {
  invalid_credentials: {
    title: "That email or password isn't right",
    detail: 'Double-check your email and password and try again.',
  },
  email_not_verified: {
    title: 'Verify your email to sign in',
    detail:
      "We sent a verification link when you signed up. Check your inbox (and spam folder) — if you can't find it, contact support.",
  },
  account_locked: {
    title: 'Too many attempts',
    detail: 'Your account is temporarily locked after repeated failed sign-ins. Try again shortly.',
  },
  // F0.5: normally intercepted by `<LoginPage>` before this table is ever
  // consulted — a live `onboarding_required` response routes straight to
  // `<CreateCompanyPage>` (see LoginPage.tsx). This entry is the defensive
  // fallback for the one case that redirect can't handle: the response is
  // missing its `csrf-token` header (a server bug, or a proxy that stripped
  // it) — see LoginPage.tsx's `handleSubmit`.
  onboarding_required: {
    title: "You're verified, but we couldn't start account setup",
    detail: 'Please try signing in again. Contact support if this keeps happening.',
  },
}

const REGISTER_PROBLEM_COPY: Record<string, ProblemCopy> = {
  password_policy: {
    title: "That password doesn't meet the password policy",
    detail: 'Use between 12 and 200 characters.',
  },
}

const VERIFY_EMAIL_PROBLEM_COPY: Record<string, ProblemCopy> = {
  invalid_token: {
    title: 'This verification link is invalid or has expired',
    detail: 'Request a new account to get a fresh verification link, or contact support.',
  },
}

const CREATE_ACCOUNT_PROBLEM_COPY: Record<string, ProblemCopy> = {
  already_onboarded: {
    title: 'You already have an account',
    detail: 'Sign in to continue — a first account can only be created once.',
  },
}

/**
 * F0.7 (`<RequestToJoinPage>`): the `POST /onboarding/join-requests` failure
 * codes (apps/api/src/modules/onboarding/routes.ts `JOIN_REQUEST_ERROR_STATUS`).
 * Both codes are DELIBERATELY neutral/non-enumerating per the contract's own
 * docstring — the copy here must not narrow that ambiguity (e.g. `conflict`
 * covers both "you already have a pending request" and "you're already a
 * member", and the SPA cannot and must not guess which).
 */
const JOIN_REQUEST_PROBLEM_COPY: Record<string, ProblemCopy> = {
  not_found: {
    title: "We couldn't verify a match for your request",
    detail:
      'This can happen if your email verification changed. Sign in again to retry, or contact support.',
  },
  conflict: {
    title: "You've already requested to join, or you're already a member",
    detail: 'Sign in to check your account status.',
  },
}

/**
 * F0.6 (`<ManageUsersPage>`): `POST`/`GET /onboarding/invitations` and
 * `POST /onboarding/invitations/{id}/revoke` failure codes
 * (apps/api/src/modules/onboarding/routes.ts `INVITATION_ERROR_STATUS`,
 * `sendInvitationProblem`). Unlike the requester-facing join-request codes
 * above, these ARE safe to narrow — the caller already holds `user:manage`
 * for their own tenant, so distinguishing "already a member" from "invite
 * already pending" does not leak anything about accounts the caller can't
 * already see.
 */
const INVITATION_PROBLEM_COPY: Record<string, ProblemCopy> = {
  validation: {
    title: "That invite couldn't be sent",
    detail: 'Check the email address and role, then try again.',
  },
  conflict: {
    title: "That invite couldn't be sent",
    detail: 'This person is already a member, or already has a pending invite.',
  },
  // Revoke's neutral 404 (NT-ONB-13): the invite may have just been accepted,
  // already revoked, or expired — the UI cannot and must not distinguish.
  not_found: {
    title: "That invite couldn't be revoked",
    detail: 'It may have already been accepted, revoked, or expired. Refresh the list to check.',
  },
}

/**
 * F0.6: `POST /onboarding/join-requests/{id}/approve|deny` failure codes
 * (`JOIN_REQUEST_ERROR_STATUS`).
 */
const JOIN_REQUEST_DECISION_PROBLEM_COPY: Record<string, ProblemCopy> = {
  validation: {
    title: "That role couldn't be granted",
    detail: 'Choose one of the available roles and try again.',
  },
  conflict: {
    title: "That request couldn't be processed",
    detail: 'It may have already been decided or withdrawn. Refresh the list to check.',
  },
  not_found: {
    title: "That request couldn't be found",
    detail: 'It may have already been decided or withdrawn. Refresh the list to check.',
  },
  self_approval: {
    title: "You can't approve your own request",
    detail: 'Ask another admin on the account to review this request.',
  },
}

/**
 * F0.8 (`<BillingPage>`): `POST /onboarding/request-po-terms`'s single
 * failure code (apps/api/openapi.yaml `requestPoTerms` 409). Deliberately
 * NEUTRAL by contract — it covers both "already qualified" and "suspended"
 * without disclosing which, so the copy here must not narrow that either
 * (this table is the fallback path when the caller lands on the request
 * form without already knowing its `lifecycleState` from the session read —
 * see BillingPage.tsx's doc comment on why the two states ARE distinguished
 * up front when the session already reveals them).
 */
const REQUEST_PO_TERMS_PROBLEM_COPY: Record<string, ProblemCopy> = {
  po_terms_unavailable: {
    title: "PO / Net Terms aren't available for this account right now",
    detail: 'Contact support if you think this is unexpected.',
  },
}

function resolve(
  table: Record<string, ProblemCopy>,
  code: string | undefined,
  fallbackDetail: string | undefined,
): ProblemCopy {
  if (code && table[code]) {
    return table[code]
  }
  return {
    title: 'Something went wrong',
    detail: fallbackDetail ?? 'Please try again.',
  }
}

export function loginProblemCopy(code: string | undefined, fallbackDetail?: string): ProblemCopy {
  return resolve(LOGIN_PROBLEM_COPY, code, fallbackDetail)
}

export function registerProblemCopy(
  code: string | undefined,
  fallbackDetail?: string,
): ProblemCopy {
  return resolve(REGISTER_PROBLEM_COPY, code, fallbackDetail)
}

export function verifyEmailProblemCopy(
  code: string | undefined,
  fallbackDetail?: string,
): ProblemCopy {
  return resolve(VERIFY_EMAIL_PROBLEM_COPY, code, fallbackDetail)
}

export function createAccountProblemCopy(
  code: string | undefined,
  fallbackDetail?: string,
): ProblemCopy {
  return resolve(CREATE_ACCOUNT_PROBLEM_COPY, code, fallbackDetail)
}

export function joinRequestProblemCopy(
  code: string | undefined,
  fallbackDetail?: string,
): ProblemCopy {
  return resolve(JOIN_REQUEST_PROBLEM_COPY, code, fallbackDetail)
}

export function invitationProblemCopy(
  code: string | undefined,
  fallbackDetail?: string,
): ProblemCopy {
  return resolve(INVITATION_PROBLEM_COPY, code, fallbackDetail)
}

export function joinRequestDecisionProblemCopy(
  code: string | undefined,
  fallbackDetail?: string,
): ProblemCopy {
  return resolve(JOIN_REQUEST_DECISION_PROBLEM_COPY, code, fallbackDetail)
}

export function requestPoTermsProblemCopy(
  code: string | undefined,
  fallbackDetail?: string,
): ProblemCopy {
  return resolve(REQUEST_PO_TERMS_PROBLEM_COPY, code, fallbackDetail)
}
